import { depthFirst } from "@lernetz/svelte-common";
import { get, type Writable } from "svelte/store";
import { Gateway } from "./Gateway";

export class ModelGateway extends Gateway {
  query<Type extends DBModel[] = DBModel[]>(queryObject: QueryObject) {
    return this.base.jsonApi("query", queryObject).then<Type>(this.in);
  }

  create<Type extends DBModel = DBModel>(
    model: {
      [key: string]: any;
      modelName: string;
    },
    relations: object = {},
  ) {
    return this.base.jsonApi("create", { model: this.out(model), relations }).then<Type>(this.in);
  }

  update<Type extends DBModel = DBModel>(model: Type, newValues: { [name: string]: any }) {
    let oldValues = {};

    Object.keys(newValues).forEach((key) => {
      let ref = get(model);

      // get old value from model
      oldValues[key] = ref[key];

      // as we have to previous value, we can apply it on the model
      ref[key] = newValues[key];
      model.set(ref); // update svelte store

      // reduce values for transmission
      newValues[key] = reduceModels(newValues[key]);
      oldValues[key] = reduceModels(oldValues[key]);
    });

    return this.base
      .jsonApi(
        "update",
        this.out({
          model: reduceModels(model),
          newValues,
          oldValues,
        }),
      )
      .then<Type>((res: any) => {
        // apply tranformations
        this.in(res);

        // return original model
        return model;
      });
  }

  delete<Type extends DBModel = DBModel>(model: Type) {
    return this.base
      .jsonApi("delete", this.out({ model: reduceModels(model) }))
      .then<Type>(this.in);
  }

  restore<Type extends DBModel = DBModel>(model: Type) {
    return this.base
      .jsonApi("restore", this.out({ model: reduceModels(model) }))
      .then<Type>(this.in);
  }

  clone<Type extends DBModel = DBModel>(model: Type) {
    return this.base.jsonApi("copy", this.out({ model: reduceModels(model) })).then<Type>(this.in);
  }
}

export type DBModel<T = { [key: string]: any }> = Writable<
  {
    modelName: string;
    id: string;
  } & T
>;

export interface QueryObject {
  where: {
    modelName: string;
    [key: string]: any;
  };
  relations?: object;
  sort?: {
    attribute: string;
    order?: string;
  };
  offset?: number;
  limit?: number;
  userdata?: boolean | string[];
}

function reduceModels(obj: any) {
  return depthFirst(obj, (i) => {
    return i?.id && i?.modelName ? { id: i.id, modelName: i.modelName } : i;
  });
}
